// Typed wrappers around localStorage. Mirrors the readJSON/writeJSON
// helpers from the vanilla profile-settings.html so behaviour matches
// exactly — same keys, same JSON shapes, same swallow-on-parse-error.

import { useEffect, useState } from 'react';

export function readJSON<T = unknown>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

export function writeJSON(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function removeKeys(keys: readonly string[]): void {
  for (const k of keys) localStorage.removeItem(k);
}

// Keys a module names after the member (kalibr.agentConvoId.<kind>.v2.<id> and
// the like): STORAGE_KEYS cannot
// list them, so sign-out sweeps every kalibr key that carries the leaving
// member's id. Never throws (a private window can refuse storage).
export function removeUserKeys(userId: string | null | undefined): void {
  if (!userId) return;
  try {
    const mine: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('kalibr') && k.includes(userId)) mine.push(k);
    }
    for (const k of mine) localStorage.removeItem(k);
  } catch { /* storage refused: nothing was kept */ }
}

// The shell's dock remembers being open across a reload (sessionStorage,
// 'ofself:dock:v1'). Signing out from the open dock must not bring it back
// open over the front door, which mounts its own shell and reads that word.
export function closeDockMemory(): void {
  try { sessionStorage.removeItem('ofself:dock:v1'); } catch { /* storage refused */ }
}

// ── Persisted-state versioning ───────────────────────────────────────
//
// Bump this whenever a change makes previously-saved kalibr* state
// incompatible with the current code — a renamed/removed key, or a changed
// value shape or default. On a bump, ensureStorageVersion() wipes all
// kalibr-owned keys at boot so old saved state can't MASK the new behaviour
// (the bug that made users manually "clear cache" after a deploy: readJSON
// only guards against unparseable JSON, not stale-but-valid shapes).
export const STORAGE_VERSION = 1;
const STORAGE_VERSION_KEY = 'kalibrStorageVersion';

/**
 * Reconcile persisted state with STORAGE_VERSION. Call ONCE at app boot,
 * before anything reads localStorage.
 *
 * - Up to date → no-op.
 * - An older/unknown version is stamped → wipe every kalibr* key (its shapes
 *   can't be trusted) and re-stamp.
 * - No version stamped yet (existing users on the first deploy of this guard,
 *   or a fresh browser) → adopt the current baseline WITHOUT wiping, so
 *   introducing the guard doesn't reset everyone. Protection kicks in from
 *   the next bump onward.
 */
export function ensureStorageVersion(): void {
  try {
    const raw = localStorage.getItem(STORAGE_VERSION_KEY);
    if (raw === String(STORAGE_VERSION)) return;
    if (raw !== null) {
      const stale: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('kalibr') && k !== STORAGE_VERSION_KEY) stale.push(k);
      }
      for (const k of stale) localStorage.removeItem(k);
    }
    localStorage.setItem(STORAGE_VERSION_KEY, String(STORAGE_VERSION));
  } catch {
    /* localStorage unavailable (private mode / disabled) — app must still boot */
  }
}

/**
 * Reactive localStorage hook. Returns [value, setValue]. The setter writes
 * to localStorage and updates the React state. Initial value falls back to
 * `fallback` if the key is absent or unparseable.
 *
 * Not meant for cross-tab sync — kalibr is a single-tab app today and the
 * cost of a `storage` event listener isn't worth it.
 */
export function useLocalStorage<T>(key: string, fallback: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => readJSON<T>(key) ?? fallback);
  // Sync once on mount in case the key changed between SSR and hydration
  // (no SSR today, but cheap to keep robust).
  useEffect(() => {
    const stored = readJSON<T>(key);
    if (stored != null) setValue(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return [
    value,
    (next: T) => {
      writeJSON(key, next);
      setValue(next);
    },
  ];
}

// ── Known keys used across the app — typed once, consumed everywhere ──

export const STORAGE_KEYS = {
  registration:    'kalibrRegistration',
  role:            'kalibrRole',
  skills:          'kalibrSkills',
  hiddenSkills:    'kalibrHiddenSkills',
  paradigmUserId:  'kalibrParadigmUserId',
  // YOUR ADDRESS (owner call 2026-10-04): the account's ofSelf username — every
  // page of yours is /@<username>/… (lib/accountBase). Written at sign-in
  // (Callback) and from /api/me/username; a name no shipped build has written.
  username:        'kalibrUsername',
  resumeNodeId:    'kalibrResumeNodeId',
  resumeFilename:  'kalibrResumeFilename',
  resumeSkills:    'kalibrResumeSkills',
  skillsCardPrefs: 'kalibrSkillsCardPrefs',
  skillsCardFeaturedSkill: 'kalibrSkillsCardFeaturedSkill',
  // THE OLD job_search_status MIRROR. It let /home open Hustle at once for a
  // settled member; nothing reads or writes it since Hustle retired
  // (2026-10-07). The name stays listed so sign-out still
  // clear what an older build wrote.
  nextStep:        'kalibrNextStep',
  step1Complete:   'kalibrStep1Complete',
  step2Complete:   'kalibrStep2Complete',
  step3Complete:   'kalibrStep3Complete',
  step4Complete:   'kalibrStep4Complete',
  // Résumé-first mirrors (ASSEMBLY_FLOW.md §0.3.7) — local reflections of the
  // server's `has_card` / `target_set` gates on /api/onboarding/status. Set
  // optimistically by the résumé-confirm / aspiration screens, re-derived from
  // the graph by Callback on every OAuth return. ADDED BESIDE the step flags —
  // the legacy keys keep their exact meaning (redefining them would bounce the
  // existing cohort back into onboarding).
  cardMinted:      'kalibrCardMinted',
  targetAnswered:  'kalibrTargetAnswered',
  // The server's word that this member is not the early cohort (its
  // `legacy_cohort: false`, mirrored by Callback): their step 2 and step 3,
  // done or still to come, are the walk's own writing, so the ladder's
  // early-member rung passes them by (lib/onboardingRoute.ts), even once this
  // browser marks a step done itself. Absent = the step flags mean what they
  // always did.
  stepsFromWalk:   'kalibrStepsFromWalk',
  // The pending-promote marker (lib/promoteResume.ts) — while present, this
  // device holds kept CV edits the graph may not have received yet.
  pendingPromote:  'kalibrPendingPromote',
  // Append-only ledger of every skill id that has ever been on this member's
  // register (lib/claimSkill.ts). The library's gate treats "had it once" like
  // holding it — remove-then-regret must never lock you out of what you
  // declared (step 9).
  everHeldSkills:  'kalibrEverHeldSkills',
  // THE CARD LAST OPEN. Nothing reads or writes it since home became all your skills (2026-10-30); listed so sign-out
  // still clears what an older build wrote.
  lastCard:        'kalibrLastCard',
  // WHICH CARD THE OLD REGISTER WAS AIMED AT — {kind, id}. Nothing reads or
  // writes it since Prove's old register (useRegisterScope.ts) went on
  // 2026-10-06; the name stays listed so sign-out still clears what an older
  // build wrote.
  //
  // NO STORAGE_VERSION bump for this: the guard exists for keys whose SHAPE or
  // MEANING changed under a name a shipped build already wrote. This name has
  // never been written, so bumping would wipe every holder's hidden set and
  // ledger mirror to introduce a view preference.
  registerCard:    'kalibrRegisterCard',
  // WHAT THE TRAY HAS SHOWN YOU AND YOU PRESSED (lib/inboxSeen.ts): row id →
  // when. A row other than a message has no read state on the server, so
  // this is its only one. Cleared on sign-out with the rest — the next
  // person's tray must not open half-read.
  inboxSeen:       'kalibrInboxSeen',
  // THE GUIDED TOUR'S ONE WORD (lib/tour.ts): 'pending' once onboarding's
  // exit queues it, 'seen' once the shell's <Tour> is done or skipped (its
  // storageKey). Absent for everyone who predates it: the legacy cohort is
  // never toured unasked; Quick Help's "Take the tour" door serves them.
  //
  // NO STORAGE_VERSION bump, for the reason registerCard gives above: this
  // name has never been written by a shipped build. A later bump wipes it
  // with everything else; only another onboarding exit can queue it again.
  tour:            'kalibrTour',
  // THE CHECKLIST'S WELCOME (lib/checklist.ts): the local day the member last
  // pressed "Got it" on the tooltip that points at the inbox. Device-local,
  // like the tour's word; a new day brings the tooltip back while that day's
  // checklist row is unread.
  checklistCoach:  'kalibrChecklistCoach',
  // THE ONBOARDING CARD STEP (docs/ONBOARDING_CARD.md §3.7, §4.2, §7.4;
  // lib/onboardingCard.ts reads and writes all three). `onboardingCard` is
  // the reload marker write 1 leaves ({user_id, card_id, parse_node_id, m,
  // hydrated}) so a reload mounts /onboarding/caliber-card at rest and write 2 can
  // find its card; `cardReviewed` is the ladder rung's mirror (Continue,
  // Skip, the manual picker, or Callback when target_set is true);
  // `cardSkillCount` is what the aim screen's subtitle counts. Never a
  // STORAGE_VERSION bump: names no shipped build has written.
  onboardingCard:  'kalibrOnboardingCard',
  cardReviewed:    'kalibrCardReviewed',
  cardSkillCount:  'kalibrCardSkillCount',
  // The card step was PASSED, not seen (the no-résumé path, the confirm
  // screen's skip): the goal screen's Previous Step goes to the résumé door.
  cardStepSkipped: 'kalibrCardStepSkipped',
  // THE FIRST CARD CHOSEN (lib/firstCard.ts): the card the Caliber step made
  // the walk's first, {user_id, card_id, library_id, name, base} — card #1,
  // with your own skills (library_id null) or a library card's in their
  // place, and the name typed for it. A new name, never a STORAGE_VERSION
  // bump.
  firstCard:       'kalibrFirstCard',
  // THE PROFILE STEP'S MIRROR (lib/onboardingRoute.ts): the platform's own
  // word on the account's profile — 'missing' (it presents none) is the one
  // value that routes anywhere (to /onboarding/profile); a profile with no
  // name walks on (owner call 2026-10-05). Written by
  // Callback from the server's profile_standing and by the
  // confirm screen when the mint refuses for want of one; cleared by the
  // profile step's save. Absent = no reason to think the step is due.
  profileStanding: 'kalibrProfileStanding',
  // THE WAY OUT (owner call 2026-09-26): 'true' once the person pressed Exit
  // onboarding — the 'post' guard stops walking them back into the flow
  // (lib/useOnboardingGuard). This browser's word only: an OAuth return
  // still routes by the server's ladder. Never a STORAGE_VERSION bump.
  onboardingExited: 'kalibrOnboardingExited',
  // THE WELCOME, SEEN (owner call 2026-09-26): 'true' once Begin was pressed
  // on /onboarding/welcome, so the ladder stops opening on it. A new name —
  // never a STORAGE_VERSION bump.
  welcomed: 'kalibrWelcomed',
} as const;

/** The signed-in member's Paradigm id, or null when nobody is signed in on
 *  this page (or the browser refuses storage). The one read of it: a caller
 *  that wants '' says `?? ''`. */
export function currentUserId(): string | null {
  try { return localStorage.getItem(STORAGE_KEYS.paradigmUserId); } catch { return null; }
}

export interface RegistrationStored {
  name?: string;
  email?: string;
}

export interface RoleStored {
  occupationId?: string;
  title?: string;
  occupation?: string;
  abbreviation?: string;
  company?: string;
  location?: string;
  locationCoords?: { lat?: string; lon?: string };
  bio?: string;
}
